import { selectIsLoggedIn } from '@/features/oAuth/oAuthSlice';
import { useGetTradingviewQuery } from '@/features/tradingview/tradingviewApi';
import Icon from '@/shared/components/Icon/Icon';
import { getTradingviewSymbol } from '@/shared/helpers/markets';
import { useWindowDimensions } from '@/shared/hooks/useWindowDimension';
import { MarketInfo } from '@cex/shared-modules';
import { IonCol, IonGrid, IonRouterLink, IonRow, IonSpinner, IonText } from '@ionic/react';
import { EntityId, EntityState } from '@reduxjs/toolkit';
import { LineWidth, createChart } from 'lightweight-charts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import styles from './MarketChart.module.css';
import { getEuroFormat, getPercentFormat, timeFormatter } from '@/shared/helpers/formatters';

type Period = {
  id: string;
  label: string;
};

const INITIAL_WIDTH = 536;
const INITIAL_HEIGHT = 350;

type NoChartViewProps = {
  isLoading?: boolean;
};

const NoChartView: React.FC<NoChartViewProps> = ({ isLoading }) => {
  const { t } = useTranslation();

  return (
    <div
      className={styles.errorWrapper}
      style={{
        height: INITIAL_HEIGHT
      }}
    >
      <IonGrid>
        <IonRow class="ion-text-center">
          <IonCol>
            {isLoading ? (
              <IonSpinner color="primary" name="crescent"></IonSpinner>
            ) : (
              <div>
                <Icon src="assets/icons/candlestick-chart.svg" color="medium" size="large" />
              </div>
            )}
          </IonCol>
        </IonRow>
        {!isLoading && (
          <IonRow class="ion-text-center">
            <IonCol>
              <IonText className={styles.label}>{t('general.market.chart_not_available')}</IonText>
            </IonCol>
          </IonRow>
        )}
      </IonGrid>
    </div>
  );
};

type MarketChartProps = {
  selectedMarket: EntityId;
  marketInfo: EntityState<MarketInfo>;
};

const getTrend = (priceChange: number): 'up' | 'down' | 'neutral' => {
  if (priceChange > 0) {
    return 'up';
  }

  if (priceChange < 0) {
    return 'down';
  }

  return 'neutral';
};

const MarketChart: React.FC<MarketChartProps> = ({ selectedMarket, marketInfo }) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const period = new URLSearchParams(location.search).get('period') || '1D';
  const symbol = marketInfo ? getTradingviewSymbol(marketInfo.entities[selectedMarket]) : undefined;

  const { name } = marketInfo?.entities[selectedMarket] || {};

  const [crosshairPrice, setCrosshairPrice] = useState<number>();

  const chartContainerRef = useRef<any>();
  const { isMobileView } = useWindowDimensions();
  const isLoggedIn = useSelector(selectIsLoggedIn);

  // TODO: can be in shared-modules and needs to be nicer!
  const resolution = period === '1D' ? '15' : '1D';
  const date = new Date();
  const now = Math.floor(date.getTime() / 1000);
  const oneDayAgo = now - 24 * 3600;
  const oneWeekAgo = now - 7 * 24 * 3600;
  const oneMonthAgo = now - 730 * 3600;
  const allTime = 1569189600; // 23.09.2019 BSDEX launch
  const from = period === '1D' ? oneDayAgo : period === '1W' ? oneWeekAgo : period === '1M' ? oneMonthAgo : allTime;

  const SELECTABLE_PERIODS: Period[] = [
    { id: '1D', label: '24H' },
    { id: '1W', label: '1W' },
    { id: '1M', label: '1M' },
    { id: 'All', label: t('market.details.chart.period_all') }
  ];

  const chartTheme = useMemo(() => {
    return {
      chart: {
        layout: {
          backgroundColor: 'transparent',
          lineColor: '#6432FA',
          textColor: '#7c7c7c'
        },
        watermark: {
          color: 'rgba(0, 0, 0, 0)'
        },
        grid: {
          vertLines: {
            color: 'transparent'
          },
          horzLines: {
            color: 'transparent'
          }
        },
        handleScale: isMobileView,
        handleScroll: isMobileView,
        priceScale: {
          autoScale: false,
          scaleMargins: {
            top: 0.1,
            bottom: 0.1
          },
          borderColor: '#e0e3e3'
        },
        timeScale: {
          fixLeftEdge: true,
          timeVisible: true,
          secondsVisible: false,
          borderColor: '#e0e3e3'
        },
        crosshair: {
          horzLine: {
            visible: false,
            labelVisible: false
          }
        },
        localization: {
          priceFormatter: (price: number) => getEuroFormat(price, 'symbol'),
          timeFormatter: timeFormatter
        }
      },
      series: {
        lastValueVisible: false,
        priceLineVisible: false
      },
      area: {
        lineColor: '#6432FA',
        lineWidth: 2 as LineWidth,
        topColor: '#6432FA',
        bottomColor: 'white'
      }
    };
  }, [isMobileView]);

  const { data: tvData, isLoading } = useGetTradingviewQuery(
    {
      ticker: selectedMarket,
      symbol,
      resolution,
      from,
      to: now
    },
    { skip: !isLoggedIn }
  );

  const chartData = useMemo(() => {
    return tvData?.entities[selectedMarket]?.chartData || null;
  }, [selectedMarket, tvData]);

  const getChartSize = useCallback(() => {
    if (!chartContainerRef.current) {
      return {
        width: INITIAL_WIDTH,
        height: INITIAL_HEIGHT
      };
    }

    const wrapperWidth = chartContainerRef.current.clientWidth;

    return {
      width: wrapperWidth,
      height: INITIAL_HEIGHT
    };
  }, []);

  useEffect(() => {
    if (!chartContainerRef.current) {
      return;
    }

    const chart = createChart(chartContainerRef.current, {
      ...getChartSize(),
      ...chartTheme.chart
    });

    chart.timeScale().fitContent();

    const chartSeries = chart.addAreaSeries({
      ...chartTheme.series,
      ...chartTheme.area
    });

    chartSeries.setData(chartData || []);

    const currentContainerRef = chartContainerRef.current;

    // Make Chart Responsive with screen resize
    // https://stackoverflow.com/a/70389132/1219557
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length === 0 || !currentContainerRef) {
        return;
      }

      const newRect = entries[0].contentRect;
      chart.applyOptions({ width: newRect.width });
    });

    resizeObserver.observe(currentContainerRef);

    const handleCrosshairMovement = (param) => {
      if (!param.point) {
        setCrosshairPrice(undefined);
      } else {
        const nextCrosshairPrice = chartData?.find((d) => d.time === param.time)?.value;
        setCrosshairPrice(nextCrosshairPrice);
      }
    };

    chart.subscribeCrosshairMove(handleCrosshairMovement);

    return () => {
      // window.removeEventListener('resize', handleResize);
      resizeObserver.unobserve(currentContainerRef);
      chart.unsubscribeCrosshairMove(handleCrosshairMovement);
      chart.remove();
    };
  }, [chartData, chartContainerRef, chartTheme, getChartSize]);

  const firstPrice = chartData ? chartData[0].value : 0;
  const lastPrice = chartData?.slice(-1)[0].value;
  const selectedPrice = crosshairPrice || lastPrice;
  const priceChange = selectedPrice - firstPrice;
  const priceChangePercent = priceChange !== 0 ? priceChange / firstPrice : 0;
  const trend = getTrend(priceChange);

  return (
    <section data-testid="market-chart" className={styles.wrapper} key={`market-chart-${name}`}>
      {(isLoading || !chartData) && <NoChartView isLoading />}
      {!isLoading && (
        <>
          <div className={styles.chartInfo}>
            <div className={styles.priceWrapper}>
              <span className={styles.price}>{getEuroFormat(selectedPrice)}</span>
              <span className={`${styles.priceChange} ${styles[trend]}`}>
                {getEuroFormat(priceChange)} ({getPercentFormat(priceChangePercent)})
              </span>
            </div>
            <div className={styles.periodWrapper}>
              {SELECTABLE_PERIODS.map(({ id, label }) => (
                <IonRouterLink
                  key={id}
                  onClick={() =>
                    history.replace({
                      pathname: location.pathname,
                      search: `detail&period=${id}`
                    })
                  }
                  className={`${period === id ? styles.active : styles.period}`}
                >
                  {label}
                </IonRouterLink>
              ))}
            </div>
          </div>
          <div id="market-chart" ref={chartContainerRef} />
        </>
      )}
    </section>
  );
};

export default MarketChart;
